"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Clock, Compass, MapPin, Navigation, Plane, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
const coverageMetrics = [{
  value: "102",
  label: "Illinois Counties",
  sub: "Full statewide pickup & drop-off"
}, {
  value: "Flat",
  label: "Rate Guarantee",
  sub: "Zero meters & no congestion surge"
}, {
  value: "3",
  label: "Border States",
  sub: "Non-stop charter into WI, IN, IA"
}, {
  value: "24/7",
  label: "Flight Tracking",
  sub: "Commercial terminals & private FBOs"
}];
const zones = [{
  id: "metro",
  name: "Chicago & Airport Hubs",
  tag: "Core Metro Network",
  summary: "Dedicated point-to-point service connecting the Downtown Loop, North Shore, Oak Brook, and suburban business centers with ORD and MDW terminals.",
  corridors: "I-90 Kennedy / Jane Addams, I-94 Edens / Dan Ryan, I-290 Eisenhower, I-88 Reagan",
  airports: ["O'Hare International (ORD) — All Terminals", "Midway International (MDW)", "Chicago Executive Airport (PWK)", "DuPage Aviation Hub (DPA)"],
  popularRoutes: [{
    from: "Downtown Chicago / Loop",
    to: "O'Hare (ORD) Terminals",
    badge: "Commercial Transfer",
    note: "Curbside or baggage meet-and-greet"
  }, {
    from: "North Shore / Lake Forest",
    to: "Midway Airport (MDW)",
    badge: "Executive Route",
    note: "Flat fare across all peak hours"
  }, {
    from: "West Suburbs / Naperville",
    to: "Private FBO Porticos",
    badge: "Aviation Charter",
    note: "Tarmac-side coordination ready"
  }]
}, {
  id: "illinois",
  name: "All 102 Illinois Counties",
  tag: "Statewide Reach",
  summary: "Comprehensive long-distance charters serving corporate campuses, university centers, medical districts, and private estates across every Illinois county.",
  corridors: "I-55 Lincoln Corridor, I-80 Transcontinental, I-39 Central Spine, I-74 Link",
  airports: ["Chicago Rockford International (RFD)", "General Wayne A. Downing Peoria (PIA)", "Central Illinois Regional (BMI)", "Abraham Lincoln Capitol Springfield (SPI)"],
  popularRoutes: [{
    from: "Chicago Metro Area",
    to: "Rockford & Winnebago Co.",
    badge: "North Corridor",
    note: "Direct highway transit with zero stops"
  }, {
    from: "O'Hare / Midway Hubs",
    to: "Peoria & Bloomington-Normal",
    badge: "Central Transit",
    note: "Pre-scheduled executive arrival"
  }, {
    from: "DuPage / Kane Counties",
    to: "Springfield Capitol Complex",
    badge: "Statewide Charter",
    note: "Single flat rate invoiced upfront"
  }]
}, {
  id: "interstate",
  name: "Tri-State Long Distance",
  tag: "Cross-Border Service",
  summary: "Direct cross-state transportation without border surcharges, connecting Northern Illinois to major metropolitan zones in Wisconsin, Indiana, and Iowa.",
  corridors: "I-94 North to WI, I-80/90 East to Indiana Tollway, I-88/80 West to Iowa Border",
  airports: ["Milwaukee Mitchell International (MKE)", "Dane County Regional Madison (MSN)", "South Bend International (SBN)", "Quad Cities International (MLI)"],
  popularRoutes: [{
    from: "Chicago Loop / North Shore",
    to: "Downtown Milwaukee & MKE",
    badge: "Wisconsin Corridor",
    note: "Continuous private ground transit"
  }, {
    from: "O'Hare International (ORD)",
    to: "Madison & Dane County, WI",
    badge: "Interstate Link",
    note: "Guaranteed flight delay adjustment"
  }, {
    from: "South Suburbs / Chicago",
    to: "Northwest Indiana & South Bend",
    badge: "Indiana Arterial",
    note: "Direct drop-off at estate or office"
  }]
}];
export default function Coverage() {
  const shouldReduceMotion = useReducedMotion();
  const [activeZone, setActiveZone] = useState(zones[0].id);
  const currentZone = zones.find(z => z.id === activeZone) || zones[0];
  return <section id="service-area" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-28">
      {/* Background radial ambiance */}
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{
      backgroundImage: "radial-gradient(circle 600px at 50% 10%, hsla(42, 65%, 54%, 0.08), transparent 70%)"
    }} />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2">
            <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary text-xs tracking-widest uppercase">
              <Navigation className="size-3 text-primary" />
              Service Coverage Network
            </Badge>
          </div>

          <h2 className="font-default mt-4 text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Every county across Illinois,{" "}
            <span className="text-primary italic font-normal">connected.</span>
          </h2>

          <p className="text-muted-foreground mt-4 text-base leading-relaxed sm:text-lg">
            Pre-scheduled flat-rate executive transportation across all 102
            Illinois counties, major commercial flight hubs, and direct
            interstate connections into Wisconsin, Indiana, and Iowa.
          </p>
        </div>

        {/* High-level Coverage Metrics Strip */}
        <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {coverageMetrics.map((metric, i) => <div key={metric.label} data-index={i} className="border-border/80 bg-card/60 relative overflow-hidden rounded-lg border p-5 shadow-md backdrop-blur-xs">
              <div className="absolute top-0 left-0 h-[1px] w-full" style={{
            background: "linear-gradient(90deg, hsla(42, 65%, 54%, 0.5) 0%, transparent 100%)"
          }} />
              <div className="font-mono text-2xl font-bold text-primary sm:text-3xl">
                {metric.value}
              </div>
              <div className="text-foreground mt-1 text-sm font-semibold">
                {metric.label}
              </div>
              <p className="text-muted-foreground mt-0.5 text-xs">
                {metric.sub}
              </p>
            </div>)}
        </div>

        {/* Main Coverage Interactive Showcase */}
        <div className="mt-12 lg:mt-16">
          {/* Zone Selector Tabs */}
          <div className="border-border/80 bg-card/80 flex flex-col gap-2 rounded-lg border p-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="px-3 py-1 text-xs font-semibold tracking-wider text-muted-foreground uppercase">
              Select Operating Zone
            </div>
            <div className="grid grid-cols-1 gap-1.5 sm:flex sm:flex-wrap">
              {zones.map((zone, i) => {
              const isActive = zone.id === activeZone;
              return <Button key={zone.id} data-index={i} type="button" variant={isActive ? "default" : "ghost"} onClick={() => setActiveZone(zone.id)} className={`h-9 rounded-md px-4 text-xs font-semibold transition-[color,background-color,border-color] ${isActive ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground"}`}>
                    <span>{zone.name}</span>
                    <span className={`ml-2 rounded-md px-1.5 py-0.5 text-[10px] font-mono ${isActive ? "bg-primary-foreground/15 text-primary-foreground" : "bg-secondary text-secondary-foreground"}`}>
                      {zone.tag}
                    </span>
                  </Button>;
            })}
            </div>
          </div>

          {/* Dual Panel Presentation: Map Schematic + Detailed Zone Specs */}
          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
            {/* Left Column: Interactive Vector Network Canvas (7 Cols) */}
            <motion.div key={activeZone} initial={shouldReduceMotion ? false : {
            opacity: 0,
            scale: 0.99
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }} className="border-border/80 bg-card/70 relative overflow-hidden rounded-xl border p-5 shadow-lg lg:col-span-7">
              {/* Top Accent Hairline */}
              <div className="absolute top-0 left-0 h-[1px] w-full" style={{
              background: "linear-gradient(90deg, hsla(42, 65%, 54%, 0.6) 0%, hsla(220, 12%, 18%, 0.1) 100%)"
            }} />

              {/* Map Header Status */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-4">
                <div className="flex items-center gap-2">
                  <div className="size-2 rounded-md bg-primary animate-pulse" />
                  <span className="text-xs font-mono font-medium text-foreground">
                    ILLINOIS + TRI-STATE DISPATCH CORRIDOR
                  </span>
                </div>
                <div className="rounded-md border border-border/80 bg-background/80 px-2.5 py-1 text-[11px] font-mono text-primary">
                  ZONE ACTIVE: {currentZone.name.toUpperCase()}
                </div>
              </div>

              {/* Vector Geographic Schematic */}
              <div className="relative mt-4 h-[340px] w-full overflow-hidden rounded-lg border border-border/70 bg-background/90 sm:h-[400px]">
                {/* Background Grid Texture */}
                <svg className="absolute inset-0 h-full w-full stroke-border/30 opacity-40 [mask-image:radial-gradient(ellipse_at_center,white,transparent_85%)]" xmlns="http://www.w3.org/2000/svg">
                  <defs>
                    <pattern id="geo-grid" width="32" height="32" patternUnits="userSpaceOnUse">
                      <path d="M 32 0 L 0 0 0 32" fill="none" strokeWidth="0.8" />
                    </pattern>
                  </defs>
                  <rect width="100%" height="100%" fill="url(#geo-grid)" />
                </svg>

                {/* Network Vector Paths & Arterials */}
                <svg className="absolute inset-0 h-full w-full" viewBox="0 0 620 400" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* Outer State Border Contour Lines (Stylized Hairstyle) */}
                  <path d="M 140 60 L 410 70 L 450 150 L 480 320 L 370 360 L 230 330 L 170 230 Z" className="stroke-border/70" strokeWidth="1.2" strokeDasharray="4 4" />

                  {/* Northern Wisconsin Corridor (I-94) */}
                  <path d="M 400 140 L 430 45" className={`transition-[stroke,stroke-width] duration-300 ${activeZone === "interstate" ? "stroke-primary" : "stroke-border/70"}`} strokeWidth={activeZone === "interstate" ? "2.5" : "1.5"} />

                  {/* Western Corridor to Quad Cities / Iowa (I-88 / I-80) */}
                  <path d="M 400 140 L 150 180" className={`transition-[stroke,stroke-width] duration-300 ${activeZone === "interstate" ? "stroke-primary" : "stroke-border/70"}`} strokeWidth={activeZone === "interstate" ? "2.5" : "1.5"} />

                  {/* Eastern Corridor to Indiana (I-80 / I-90 / I-65) */}
                  <path d="M 460 180 L 560 210" className={`transition-[stroke,stroke-width] duration-300 ${activeZone === "interstate" ? "stroke-primary" : "stroke-border/70"}`} strokeWidth={activeZone === "interstate" ? "2.5" : "1.5"} />

                  {/* Central Illinois Spine (I-55 / I-39 / I-74) */}
                  <path d="M 400 140 L 320 240 L 280 330" className={`transition-[stroke,stroke-width] duration-300 ${activeZone === "illinois" ? "stroke-primary" : "stroke-border/70"}`} strokeWidth={activeZone === "illinois" ? "2.5" : "1.5"} />

                  {/* Greater Chicago Metro Perimeter Web */}
                  <circle cx="400" cy="140" r="44" className={`transition-[stroke,fill] duration-300 ${activeZone === "metro" ? "stroke-primary fill-primary/10" : "stroke-border/60 fill-transparent"}`} strokeWidth="1.5" />

                  {/* Core Waypoint Dots */}
                  {/* Chicago Hub (Primary) */}
                  <circle cx="400" cy="140" r="7" className="fill-primary" />
                  <circle cx="400" cy="140" r="12" className="stroke-primary/50" strokeWidth="1" />

                  {/* O'Hare (ORD) */}
                  <circle cx="375" cy="125" r="4.5" className="fill-foreground" />
                  {/* Midway (MDW) */}
                  <circle cx="390" cy="160" r="4.5" className="fill-foreground" />
                  {/* Milwaukee, WI */}
                  <circle cx="430" cy="45" r="4" className={activeZone === "interstate" ? "fill-primary" : "fill-chart-2"} />
                  {/* Rockford, IL */}
                  <circle cx="290" cy="90" r="4" className={activeZone === "illinois" ? "fill-primary" : "fill-chart-2"} />
                  {/* Quad Cities, IA */}
                  <circle cx="150" cy="180" r="4" className={activeZone === "interstate" ? "fill-primary" : "fill-chart-2"} />
                  {/* Peoria, IL */}
                  <circle cx="280" cy="235" r="4" className={activeZone === "illinois" ? "fill-primary" : "fill-chart-2"} />
                  {/* Springfield, IL */}
                  <circle cx="280" cy="330" r="4" className={activeZone === "illinois" ? "fill-primary" : "fill-chart-2"} />
                  {/* NW Indiana */}
                  <circle cx="460" cy="180" r="4" className={activeZone === "interstate" ? "fill-primary" : "fill-chart-2"} />
                </svg>

                {/* Overlay Interactive Waypoint Badges */}
                {/* Chicago Central Node */}
                <div className="absolute top-[28%] right-[25%] flex flex-col items-center">
                  <span className="flex size-3 items-center justify-center">
                    <span className="bg-primary/75 absolute inline-flex size-3 animate-ping rounded-lg opacity-75" />
                    <span className="bg-primary relative inline-flex size-2 rounded-lg" />
                  </span>
                  <div className="mt-1 rounded-md border border-border bg-background/95 px-2 py-0.5 text-[10px] font-semibold text-foreground shadow-xs">
                    Chicago Hub (ORD & MDW)
                  </div>
                </div>

                {/* Milwaukee Marker */}
                <div className="absolute top-[8%] right-[21%]">
                  <div className="rounded-md border border-border/80 bg-background/90 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
                    Milwaukee, WI
                  </div>
                </div>

                {/* Quad Cities Marker */}
                <div className="absolute top-[42%] left-[12%]">
                  <div className="rounded-md border border-border/80 bg-background/90 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
                    Quad Cities, IA
                  </div>
                </div>

                {/* Central IL / Springfield Marker */}
                <div className="absolute bottom-[14%] left-[36%]">
                  <div className="rounded-md border border-border/80 bg-background/90 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
                    Springfield, IL
                  </div>
                </div>

                {/* Northwest Indiana Marker */}
                <div className="absolute top-[44%] right-[8%]">
                  <div className="rounded-md border border-border/80 bg-background/90 px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground">
                    NW Indiana
                  </div>
                </div>

                {/* Legend pill */}
                <div className="absolute bottom-3 right-3 flex items-center gap-3 rounded-md border border-border/80 bg-card/90 px-3 py-1.5 text-[10px] font-mono text-muted-foreground backdrop-blur-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="size-2 rounded-lg bg-primary" /> Active Hub
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="size-2 rounded-lg bg-chart-2" /> Regional Node
                  </span>
                </div>
              </div>

              {/* Geographic Corridor Footnote */}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Compass className="size-3.5 text-primary" />
                  <span className="text-foreground/90 font-medium">Primary Arterials:</span>
                  <span>{currentZone.corridors}</span>
                </div>
              </div>
            </motion.div>

            {/* Right Column: Zone Highlights & Popular Flat Routes (5 Cols) */}
            <motion.div key={`${activeZone}-details`} initial={shouldReduceMotion ? false : {
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }} className="flex flex-col gap-4 lg:col-span-5">
              {/* Zone Narrative Card */}
              <Card className="border-border/80 bg-card/70 py-5 shadow-md">
                <CardContent className="space-y-3 px-5 sm:px-6">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="bg-primary/15 text-primary flex size-7 items-center justify-center rounded-md">
                        <MapPin className="size-4" />
                      </div>
                      <h3 className="font-default text-base font-semibold text-foreground sm:text-lg">
                        {currentZone.name}
                      </h3>
                    </div>
                    <Badge variant="secondary" className="bg-secondary text-secondary-foreground text-xs font-mono">
                      {currentZone.tag}
                    </Badge>
                  </div>

                  <p className="text-muted-foreground text-xs leading-relaxed sm:text-sm">
                    {currentZone.summary}
                  </p>

                  <div className="border-border/60 border-t pt-3">
                    <div className="text-foreground/90 mb-2 flex items-center gap-1.5 text-xs font-semibold">
                      <Plane className="size-3.5 text-primary" />
                      Key Aviation Access Points:
                    </div>
                    <ul className="grid grid-cols-1 gap-1.5 text-xs text-muted-foreground">
                      {currentZone.airports.map((airport, i) => <li key={airport} data-index={i} className="flex items-center gap-2">
                          <span className="size-1 rounded-lg bg-primary" />
                          <span>{airport}</span>
                        </li>)}
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Sample Popular Fixed Routes */}
              <div className="border-border/80 bg-card/60 rounded-xl border p-5 shadow-md">
                <div className="flex items-center justify-between pb-3">
                  <h4 className="text-foreground text-xs font-semibold tracking-wider uppercase">
                    Sample Zone Transits
                  </h4>
                  <span className="text-[11px] font-mono text-primary">
                    FLAT PRICED
                  </span>
                </div>

                <div className="space-y-2.5">
                  {currentZone.popularRoutes.map((route, i) => <div key={route.from + route.to} data-index={i} className="border-border/60 bg-background/60 hover:border-primary/40 rounded-lg border p-3 transition-[border-color,background-color] duration-200">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-foreground text-xs font-semibold">
                          {route.from}
                        </div>
                        <Badge variant="outline" className="border-primary/30 bg-primary/10 text-[10px] text-primary">
                          {route.badge}
                        </Badge>
                      </div>
                      <div className="text-muted-foreground mt-1 flex items-center gap-1.5 text-xs">
                        <ArrowRight className="size-3 text-primary" />
                        <span className="text-foreground/90 font-medium">
                          {route.to}
                        </span>
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        {route.note}
                      </div>
                    </div>)}
                </div>
              </div>

              {/* Service Guarantee Highlight */}
              <div className="border-border/80 bg-card/50 flex items-start gap-3 rounded-lg border p-4 shadow-sm">
                <div className="bg-primary/15 text-primary mt-0.5 rounded-md p-1.5">
                  <ShieldCheck className="size-4" />
                </div>
                <div className="space-y-0.5">
                  <div className="text-foreground text-xs font-semibold">
                    Absolute Flat-Rate Assurance
                  </div>
                  <p className="text-muted-foreground text-xs leading-relaxed">
                    No mileage meters, no peak surge spikes, and no out-of-zone
                    hidden penalties. The rate confirmed before departure is the
                    final amount paid.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Booking Action Banner */}
        <div className="border-border/80 bg-card/60 mt-12 flex flex-col items-center justify-between gap-6 rounded-xl border p-6 shadow-lg backdrop-blur-xs sm:flex-row sm:p-8">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="font-default text-lg font-bold text-foreground sm:text-xl">
              Need a pre-scheduled transfer across Illinois or neighboring states?
            </h3>
            <p className="text-muted-foreground text-xs sm:text-sm">
              Instant upfront quotes with zero surge pricing and flight schedule
              monitoring.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-6 font-semibold">
              <Link to="/OrderPage">
                Reserve your ride
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-border text-foreground hover:bg-secondary/50 h-10 px-5 font-semibold">
              <Link to="/AboutUsPage">Executive Standards</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}